import React, { Fragment, useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { MdClose } from 'react-icons/md';

import { moment } from '../../utils/timeUtils';
import { stringToColor } from '../../utils/functions';
import { TextArea } from '../TextArea';
import { Button } from '../Button/Button';
import classes from './NoteCard.module.css';
import { FaStop, FaMicrophone } from 'react-icons/fa';

const NoteCard = ({
  creator,
  message,
  date,
  isRemove = false,
  onRemove,
  isEdit = false,
  setMessage,
  onClose,
  sending,
  submit,
  onEdit,
  showEditButton,
}) => {
  const [isListening, setIsListening] = useState(false);
  const recognition = useRef(null);
  const [error, setError] = useState('');

  const startListening = () => {
    if (recognition.current && !isListening) {
      setError('');
      recognition.current.start();
      setIsListening(true);
    }
  };

  const stopListening = () => {
    if (recognition.current && isListening) {
      recognition.current.stop();
      setIsListening(false);
    }
  };

  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setError('Speech recognition not supported in this browser. Please use Chrome.');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognition.current = new SpeechRecognition();
    
    recognition.current.continuous = true;
    recognition.current.interimResults = true;
    recognition.current.lang = 'en-US';
    
    // Handle results
    recognition.current.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript + ' ';
        } else {
          interimTranscript += result[0].transcript;
        }
      }
      
      if (finalTranscript) {
        setMessage(prev => prev + finalTranscript);
      }
    };
    
    // Handle errors
    recognition.current.onerror = (event) => {
      setError(`Error: ${event.error}`);
      setIsListening(false);
    };
    
    // Handle end of recognition
    recognition.current.onend = () => {
      setIsListening(false);
    };
    
    return () => {
      if (recognition.current) {
        recognition.current.stop();
      }
    };
  }, []);

  return (
    <Box sx={{ minWidth: 275, marginBottom: '5px' }}>
      <Card variant="outlined">
        <Fragment>
          <CardContent>
            <div className={classes.header}>
              <div
                style={{
                  marginBottom: '0.5em',
                  fontSize: '1.2em',
                  color: stringToColor(`${creator}`),
                  cursor: 'pointer',
                }}
              >
                {creator}
              </div>
              <div className="d-flex align-content-center gap-2">
                {showEditButton && (
                  <div className={classes.btnDiv} onClick={onEdit}>
                    Edit
                  </div>
                )}
                {isRemove && (
                  <div className={classes.btnDiv} onClick={onRemove}>
                    Remove
                  </div>
                )}
              </div>
              {isEdit && (
                <div className={classes.btnDiv} onClick={onClose}>
                  <MdClose size={30} />
                </div>
              )}
            </div>

            {isEdit ? (
              <>
                <TextArea
                  setter={setMessage}
                  value={message ? message[0].toUpperCase() + message.slice(1) : ''}
                  placeholder={'Input Message'}
                  rows={2}
                  autoFocus
                />
                <div>
                  <IconButton
                    IconButton
                    onClick={startListening}
                    sx={{ height: 30, width: 30, color: '#1976d2' }}
                    disabled={isListening || !!error}
                    size="small"
                  >
                    <FaMicrophone fontSize="medium" />
                  </IconButton>
                  <IconButton
                    IconButton
                    sx={{ height: 30, width: 30, color: '#d32f2f' }}
                    size="small"
                    disabled={!isListening}
                    onClick={stopListening}
                  >
                    <FaStop fontSize="medium" />
                  </IconButton>
                </div>
              </>
            ) : (
              <div className={classes.msgContainer}>{message}</div>
            )}

            <div
              style={{
                fontSize: '0.8em',
                color: 'var(--dashboard-main-color)',
                cursor: 'pointer',
              }}
            >
              🕑 {moment(date).format('MM-DD-YYYY hh:mm')}
            </div>
            {isEdit && (
              <div className={classes.btnMain}>
                <Button
                  onClick={submit}
                  className={classes.btn}
                  label={`Submit`}
                  disabled={sending}
                />
              </div>
            )}
          </CardContent>
        </Fragment>
      </Card>
    </Box>
  );
};

export default NoteCard;
